<template>
    <div class="profile-page">
        <LoadingBar v-if="loading" message="Loading Purchase Order..." />

        <div v-else class="container-xl">
            <!-- Header Section -->
            <div class="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3 mb-4">
                <nav aria-label="breadcrumb">
                    <ol class="breadcrumb mb-0 bg-transparent p-0">
                        <li class="breadcrumb-item">
                            <a href="#" @click.prevent="goToPOList"
                                class="text-decoration-none text-secondary small fw-medium">
                                <i class="fa fa-arrow-left me-1"></i> Purchase Orders
                            </a>
                        </li>
                        <li class="breadcrumb-item active text-dark small fw-semibold" aria-current="page">
                            {{ order.external_id || 'PO Details' }}
                        </li>
                    </ol>
                </nav>

                <div class="d-flex align-items-center gap-2">
                    <PoStatus :status="order.status" />
                </div>
            </div>

            <!-- PO Header Card -->
            <div class="bg-white rounded-4 border border-light-subtle p-4 mb-4 shadow-sm d-flex justify-content-between flex-column flex-md-row gap-3 align-items-md-center">
                <div class="row align-items-center g-3">
                    <div class="col-md-auto">
                        <div class="bg-primary bg-gradient rounded-3 text-white d-flex align-items-center justify-content-center shadow-sm"
                            style="width: 56px; height: 56px;">
                            <span class="material-symbols-outlined fs-13 text-light">shopping_cart</span>
                        </div>
                    </div>
                    <div class="col-md">
                        <h3 class="mb-1 text-dark fw-bold text-start tracking-tight">{{ order.name || order.external_id
                            || 'Purchase Order' }}</h3>
                        <div class="d-flex flex-wrap gap-3 text-secondary small">
                            <span><i class="fa fa-hashtag me-1 opacity-75"></i>{{ order.external_id || 'N/A' }}</span>
                            <span class="text-dark opacity-25">|</span>
                            <span><i class="fa fa-calendar me-1 opacity-75"></i>{{ formatDate(order.created_at)
                                }}</span>
                        </div>
                    </div>
                </div>
                <div class="profile-actions">
                  <button type="button" class="action-btn action-btn-back" @click="$router.back()">
                        <i class="fa fa-arrow-left"></i> Back
                    </button>
                    <button type="button" class="action-btn action-btn-primary"
                        @click="editOrder(order.external_id)">
                        <i class="fa fa-pencil"></i> Edit
                    </button>
                </div>
            </div>

            <div class="row g-4 mb-4 align-items-stretch">
                <div class="col-md-8">
                    <div class="row g-3 mb-3">
                        <div class="col-6">
                            <div class="bg-white rounded-4 border border-light-subtle p-3 h-100 d-flex align-items-center shadow-sm">
                                <div class="rounded-3 bg-primary-subtle p-2 me-3 text-primary d-flex align-items-center justify-content-center"
                                    style="width: 42px; height: 42px;">
                                    <i class="fa fa-shopping-bag fs-5"></i>
                                </div>
                                <div>
                                    <div class="text-dark small fw-semibold">Total Items</div>
                                    <div class="fs-4  text-dark mt-1">{{ order.items?.length || 0 }}</div>
                                </div>
                            </div>
                        </div>
                        <div class="col-6">
                            <div class="bg-white rounded-4 border border-light-subtle p-3 h-100 d-flex align-items-center shadow-sm">
                                <div class="rounded-3 bg-success-subtle p-2 me-3 text-success d-flex align-items-center justify-content-center"
                                    style="width: 42px; height: 42px;">
                                    <i class="fa-solid fa-hand-holding-dollar fs-5"></i>
                                </div>
                                <div>
                                    <div class="text-dark small fw-semibold">Total Amount</div>
                                    <div class="fs-4 fw-bold text-success mt-1">{{ formatCurrency(order.total_price) }}</div>
                                </div>
                            </div>
                        </div>
                        <div class="col-6">
                            <div class="bg-white rounded-4 border border-light-subtle p-3 h-100 d-flex align-items-center shadow-sm">
                                <div class="rounded-3 bg-warning-subtle p-2 me-3 text-warning d-flex align-items-center justify-content-center"
                                    style="width: 42px; height: 42px;">
                                    <i class="fa fa-truck fs-5"></i>
                                </div>
                                <div>
                                    <div class="text-dark small fw-semibold">Expected Delivery</div>
                                    <div class="fs-6 text-dark mt-2 text-truncate">{{ order.expected_delivery_date ?
                                        formatDate(order.expected_delivery_date) : 'N/A' }}</div>
                                </div>
                            </div>
                        </div>
                        <div class="col-6">
                            <div class="bg-white rounded-4 border border-light-subtle p-3 h-100 d-flex align-items-center shadow-sm">
                                <div class="rounded-3 bg-info-subtle p-2 me-3 text-info d-flex align-items-center justify-content-center"
                                    style="width: 42px; height: 42px;">
                                    <i class="fa fa-check-circle fs-5"></i>
                                </div>
                                <div>
                                    <div class="text-dark small fw-semibold">Fulfillment Status</div>
                                    <div class="mt-2">
                                        <PoStatus :status="order.status" />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="bg-white rounded-4 border border-light-subtle p-4 shadow-sm ">
                        <div class="d-flex align-items-center gap-2 pb-3 mb-4 border-bottom border-light-subtle">
                            <i class="fa fa-info-circle text-primary fs-5"></i>
                            <h5 class="mb-0 fw-bold text-dark">PO Timeline</h5>
                        </div>
                        <div class="row g-4">
                            <div class="col-sm-6 col-md-4">
                                <div class="text-dark small mb-1">PO Date</div>
                                <div class="text-dark fw-semibold">{{ formatDate(order.created_at) || 'N/A' }}</div>
                            </div>
                            <div class="col-sm-6 col-md-4">
                                <div class="text-dark small mb-1">Expected Delivery</div>
                                <div class="text-warning fw-semibold">{{ formatDate(order.expected_delivery_date) ||
                                    'N/A' }}</div>
                            </div>
                            <div class="col-sm-6 col-md-4">
                                <div class="text-dark small mb-1">Actual Delivery</div>
                                <div class="text-success fw-semibold">{{ formatDate(order.actual_delivery_date) || 'N/A' }}</div>
                            </div>
                            <div class="col-sm-6 col-md-4">
                                <div class="text-dark small mb-1">Rejection Remarks</div>
                                <div class="text-danger">{{ order.rejection_remarks || 'N/A' }}</div>
                            </div>
                            <div class="col-sm-6 col-md-4">
                                <div class="text-dark small mb-1">Return Date</div>
                                <div class="text-success fw-semibold">{{ formatDate(order.return_date) || 'N/A' }}</div>
                            </div>
                            <div class="col-sm-6 col-md-4">
                                <div class="text-dark small mb-1">Return Remarks</div>
                                <div class="text-dark">{{ order.return_remarks || 'N/A' }}</div>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="col-md-4 d-flex flex-column gap-4">
                    <div class="bg-white rounded-4 border border-light-subtle p-4 shadow-sm h-100" v-if="order.received_by">
                        <div class="d-flex align-items-center gap-2 pb-3 mb-3 border-bottom border-light-subtle">
                            <i class="fa fa-user-check text-success fs-5"></i>
                            <h5 class="mb-0 fw-bold text-dark">Receiver Detail</h5>
                        </div>
                        <div class="mb-3">
                            <div class="d-flex align-items-center gap-2">
                                <div class="bg-success bg-opacity-10 text-success rounded-circle d-flex align-items-center justify-content-center fw-bold small"
                                    style="width:32px; height:32px;">
                                    {{ getFullName(order.received_by).charAt(0).toUpperCase() }}
                                </div>
                                <span class="text-dark">{{ getFullName(order.received_by) }}</span>
                            </div>
                        </div>
                        <div class="bg-light p-3 rounded-3 border border-light-subtle">
                            <p class="text-dark small mb-0 lh-base italic">"{{ order.receiver_notes || 'No notes appended by receiver.' }}"</p>
                        </div>
                    </div>
                    <div v-else>
                        <div class="bg-white rounded-4 border border-light-subtle p-4 shadow-sm h-100 d-flex flex-column justify-content-center align-items-center text-center">
                            <i class="fa fa-user-clock text-secondary fs-1 mb-3"></i>
                            <p class="text-muted small mb-0">No receiver details available.</p>
                        </div>
                    </div>

                    <div class="bg-white rounded-4 border border-light-subtle p-4 shadow-sm h-100" v-if="order.verified_by">
                        <div class="d-flex align-items-center gap-2 pb-3 mb-3 border-bottom border-light-subtle">
                            <i class="fa fa-shield text-info fs-5"></i>
                            <h5 class="mb-0 fw-bold text-dark">Verified By</h5>
                        </div>
                        <div class="mb-3">
                            <div class="d-flex align-items-center gap-2">
                                <div class="bg-info bg-opacity-10 text-info rounded-circle d-flex align-items-center justify-content-center fw-bold small"
                                    style="width:32px; height:32px;">
                                    {{ getFullName(order.verified_by).charAt(0).toUpperCase() }}
                                </div>
                                <span style="font-size:13px;font-weight:500;color:var(--pp-text-primary);">
                                    {{ getFullName(order.verified_by) }}
                                </span>
                            </div>
                        </div>
                        <div class="bg-light p-3 rounded-3 border border-light-subtle">
                            <p class="text-dark small mb-0 lh-base italic">"{{ order.approver_notes || 'No compliance remarks noted.' }}"</p>
                        </div>
                    </div>
                    <div v-else>
                        <div class="bg-white rounded-4 border border-light-subtle p-4 shadow-sm h-100 d-flex flex-column justify-content-center align-items-center text-center">
                            <i class="fa fa-user-shield text-secondary fs-1 mb-3"></i>  
                            <p class="text-muted small mb-0">No verification details available.</p>
                        </div>
                    </div>
                </div>
            </div>

            <!-- PO Teams Card - col-md-12 -->
            <div class="row g-4 mb-4" v-if="receiverTeam.length || verificationTeam.length">
                <div class="col-12">
                    <div class="bg-white rounded-4 border border-light-subtle p-4 shadow-sm">
                        <div class="d-flex align-items-center gap-2 pb-3 mb-3 border-bottom border-light-subtle">
                            <i class="fa fa-users text-primary fs-5"></i>
                            <h5 class="mb-0 fw-bold text-dark">PO Teams</h5>
                            <span class="badge bg-secondary bg-opacity-10 text-secondary border border-secondary-subtle rounded-pill px-2.5 py-1 fw-medium">{{ receiverTeam.length + verificationTeam.length }} members</span>
                        </div>
                        <div v-if="receiverTeam.length" class="mb-4">
                            <div class="text-secondary small mb-2 text-uppercase fw-semibold">Receiver Team</div>
                            <div class="d-flex flex-wrap gap-2">
                                <span v-for="user in receiverTeam" :key="user.id || user.user_id" class="badge bg-secondary bg-opacity-10 text-dark border border-secondary-subtle rounded-pill px-2 py-1">
                                    {{ getFullName(user) }}
                                </span>
                            </div>
                        </div>
                        <div v-if="verificationTeam.length" class="mb-0">
                            <div class="text-secondary small mb-2 text-uppercase fw-semibold">Verification Team</div>
                            <div class="d-flex flex-wrap gap-2">
                                <span v-for="user in verificationTeam" :key="user.id || user.user_id" class="badge bg-secondary bg-opacity-10 text-dark border border-secondary-subtle rounded-pill px-2 py-1">
                                    {{ getFullName(user) }}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Vendor Card - col-md-12 -->
            <div class="row g-4 mb-4">
                <div class="col-12">
                    <div class="bg-white rounded-4 border border-light-subtle p-4 shadow-sm">
                        <div class="d-flex align-items-center gap-2 pb-3 mb-4 border-bottom border-light-subtle">
                            <i class="fa fa-building text-primary fs-5"></i>
                            <h5 class="mb-0 fw-bold text-dark">Vendor -
                                <router-link v-if="getVendorRoute(order.vendor)" :to="getVendorRoute(order.vendor)" class="text-decoration-none text-primary fw-semibold">
                                    {{ vendorName }}
                                </router-link>
                                <span v-else style="font-weight: 400;">{{ vendorName }}</span>
                            </h5>
                        </div>
                        
                        <div class="table-responsive border rounded-3">
                            <table class="table table-sm table-borderless align-middle mb-0">
                                <thead class="text-uppercase small text-secondary">
                                    <tr class="border-bottom border-light">
                                        <th class="py-2 ps-3" style="font-size: 0.8rem; letter-spacing: 0.5px;">Name</th>
                                        <th class="py-2" style="font-size: 0.8rem; letter-spacing: 0.5px;">Email</th>
                                        <th class="py-2" style="font-size: 0.8rem; letter-spacing: 0.5px;">Role</th>
                                        <th class="py-2 pe-3" style="font-size: 0.8rem; letter-spacing: 0.5px;">Phone</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="(contact, idx) in contactList" :key="idx" class="border-bottom border-light">
                                        <td class="ps-3 py-2">
                                            <span class="text-dark">{{ contact.vendor_contact?.name || 'N/A' }}</span>
                                        </td>
                                        <td class="py-2">
                                            <a :href="`mailto:${contact.vendor_contact?.email}`" class="text-decoration-none text-primary">
                                                {{ contact.vendor_contact?.email || 'N/A' }}
                                            </a>
                                        </td>
                                        <td class="py-2">
                                            <span class="badge bg-primary-subtle bg-opacity-10 text-secondary border border-secondary-subtle px-2 py-1 rounded small">
                                                {{ contact.vendor_contact?.role || 'N/A' }}
                                            </span>
                                        </td>
                                        <td class="pe-3 py-2">
                                            <a :href="`tel:${contact.vendor_contact?.phone}`" class="text-decoration-none small text-dark">
                                                {{ contact.vendor_contact?.phone || 'N/A' }}
                                            </a>
                                        </td>
                                    </tr>
                                    <tr v-if="!contactList.length">
                                        <td colspan="4" class="text-center text-dark opacity-50 py-4 small italic">
                                            No contacts available for this vendor.
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>

            <!-- PO Items - col-md-12 -->
            <div class="row g-4 mb-4">
                <div class="col-12">
                    <div class="bg-white rounded-4 border border-light-subtle shadow-sm overflow-hidden">
                        <div
                            class="d-flex justify-content-between align-items-center p-4 border-bottom border-light-subtle bg-light bg-opacity-25">
                            <div class="d-flex align-items-center gap-2">
                                <i class="fa fa-list text-primary fs-5"></i>
                                <h5 class="mb-0 fw-bold text-dark">PO Items Line Overview</h5>
                            </div>
                            <span
                                class="badge bg-primary bg-opacity-10 text-light border border-primary-subtle rounded-pill px-3 py-1.5 fw-semibold">{{
                                order.items?.length || 0 }} Line Items</span>
                        </div>
                        <div class="p-3">
                            <div v-if="order.items?.length" class="profile-item-cards">
                                <div v-for="(item, index) in order.items" :key="item.id || index" class="profile-item-card mb-3">
                                    <div class="profile-item-card-header d-flex flex-column flex-md-row justify-content-between gap-3 align-items-start">
                                        <div class="profile-item-info d-flex gap-3 align-items-start flex-grow-1">
                                            <span class="item-number">{{ index + 1 }}</span>
                                            <div>
                                                <router-link v-if="getItemRoute(item)" :to="getItemRoute(item)" class="item-name mb-1 text-decoration-none text-dark fw-semibold">
                                                    {{ getItemName(item) }}
                                                </router-link>
                                                <span v-else class="item-name mb-1 d-inline-block">{{ getItemName(item) }}</span>
                                                <div class="item-code text-muted small">SKU-REF: {{ getItemCode(item) || 'N/A' }}</div>
                                            </div>
                                        </div>
                                        <div class="profile-item-summary d-flex flex-wrap gap-3 text-center align-items-center">
                                            <div class="summary-block">
                                                <div class="summary-label">Qty</div>
                                                <div class="summary-value">{{ item.quantity }} {{ item.unit || 'pcs' }}</div>
                                            </div>
                                            <div class="summary-block">
                                                <div class="summary-label">Unit Rate</div>
                                                <div class="summary-value">{{ formatCurrency(item.unit_price) }}</div>
                                            </div>
                                            <div class="summary-block">
                                                <div class="summary-label">Line Total</div>
                                                <div class="summary-value">{{ formatCurrency(item.total_price) }}</div>
                                            </div>
                                        </div>
                                    </div>
                                    <div class="profile-item-card-body p-3 border-top">
                                        <div v-if="String(item.material_type).toLowerCase() === 'chemical' || String(item.material_type).toLowerCase() === 'part'" class="d-flex flex-wrap align-items-center gap-4 mb-3">
                                            <template v-if="String(item.material_type).toLowerCase() === 'chemical'">
                                                <div class="d-flex align-items-center gap-2 text-dark">
                                                    <i :class="item.sds === 'yes' ? 'fa fa-check-circle text-success' : 'fa fa-times-circle text-danger'"></i>
                                                    <span>Take Safety Data Sheet (SDS) at receiving PO? <span class="fw-semibold">{{ item.sds === 'yes' ? 'Yes' : 'No' }}</span></span>
                                                </div>
                                                <div class="d-flex align-items-center gap-2 text-dark">
                                                    <i :class="item.approved_chemical === 'yes' ? 'fa fa-check-circle text-success' : 'fa fa-times-circle text-danger'"></i>
                                                    <span>Is it Approved Chemical? <span class="fw-semibold">{{ item.approved_chemical === 'yes' ? 'Yes' : 'No' }}</span></span>
                                                </div>
                                                <div class="d-flex align-items-center gap-2 text-dark">
                                                    <i :class="item.food_grade_chemical === 'yes' ? 'fa fa-check-circle text-success' : 'fa fa-times-circle text-danger'"></i>
                                                    <span>Is it Food Grade chemical? <span class="fw-semibold">{{ item.food_grade_chemical === 'yes' ? 'Yes' : 'No' }}</span></span>
                                                </div>
                                                <div class="d-flex align-items-center gap-2 text-dark">
                                                    <span>SDS File:</span>
                                                    <span>
                                                        <a v-if="item.sds_attachment" :href="resolveFileUrl(item.sds_attachment)" target="_blank" class="text-primary text-decoration-none">View SDS</a>
                                                        <span v-else class="text-muted">N/A</span>
                                                    </span>
                                                </div>
                                            </template>
                                            <template v-else-if="String(item.material_type).toLowerCase() === 'part'">
                                                <div class="d-flex align-items-center gap-2 text-dark">
                                                    <i :class="item.food_contact_part === 'yes' ? 'fa fa-check-circle text-success' : 'fa fa-times-circle text-danger'"></i>
                                                    <span>Is it Food Contact part? <span class="fw-semibold">{{ item.food_contact_part === 'yes' ? 'Yes' : 'No' }}</span></span>
                                                </div>
                                            </template>
                                        </div>
                                        <div v-if="item.checklists && item.checklists.length" class="verify-checklist-card p-3">
                                            <div class="verify-checklist-title mb-3">Equipment Sanitation Checklist</div>
                                            <div class="verify-checklist-grid">
                                                <div v-for="(check, cIdx) in item.checklists" :key="check.id || cIdx" class="verify-checklist-row">
                                                    <div class="verify-checklist-index">{{ cIdx + 1 }}</div>
                                                    <div class="verify-checklist-label">{{ check.label }}</div>
                                                    <div class="verify-checklist-value">
                                                        <span :class="['badge', check.value === 'yes' ? 'bg-success' : check.value === 'no' ? 'bg-danger' : 'bg-warning text-dark']">
                                                            {{ check.value === 'na' ? 'N/A' : check.value }}
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div v-else class="text-center py-5 text-dark bg-transparent">
                                <i class="fa fa-cube fa-2x mb-2 text-dark opacity-25"></i>
                                <p class="mb-0 small fw-semibold opacity-75">No lines cataloged in this Purchase Order.</p>
                            </div>
                        </div>
                        <div class="d-flex justify-content-end align-items-center p-3 bg-light bg-opacity-25 border-top border-light-subtle">
                            <div class="d-flex align-items-center gap-4">
                                <span class="text-dark fw-bold uppercase tracking-wider">Aggregate Sum Total:</span>
                                <span class="fs-3 fw-black text-primary">{{ formatCurrency(order.total_price) }}</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Document Attachments - col-md-12 -->
            <div class="row g-4 mb-4">
                <div class="col-12">
                    <div class="bg-white rounded-4 border border-light-subtle p-4 shadow-sm">
                        <div
                            class="d-flex justify-content-between align-items-center pb-3 mb-4 border-bottom border-light-subtle">
                            <div class="d-flex align-items-center gap-2">
                                <i class="fa fa-paperclip text-primary fs-5"></i>
                                <h5 class="mb-0 fw-bold text-dark">Document Attachments</h5>
                            </div>
                            <span
                                class="badge bg-secondary bg-opacity-10 text-secondary border border-secondary-subtle rounded-pill px-2.5 py-1 fw-medium">{{
                                    imageList.length + fileList.length }} files</span>
                        </div>

                        <div v-if="imageList.length || fileList.length" class="row g-3">
                            <div v-for="(image, index) in imageList" :key="'img-' + (image.id || index)"
                                class="col-6 col-sm-4 col-md-3">
                                <div class="border border-light-subtle rounded-3 overflow-hidden c-pointer shadow-sm-hover transition-all bg-light"
                                    @click="previewMedia(image, 'image')">
                                    <div
                                        class="ratio ratio-16x9 bg-white border-bottom border-light-subtle d-flex align-items-center justify-content-center overflow-hidden">
                                        <img :src="resolveFileUrl(image.file_path)" :alt="image.file_name || 'Image'"
                                            class="object-fit-cover w-100 h-100" />
                                    </div>
                                    <div class="p-2.5 bg-white">
                                        <div class="text-dark small fw-bold text-truncate mb-0.5">{{ image.file_name ||
                                            `Image ${index + 1}` }}</div>
                                        <div class="text-dark opacity-50" style="font-size: 0.7rem;">{{
                                            formatFileSize(image.file_size) }}</div>
                                    </div>
                                </div>
                            </div>

                            <div v-for="(file, index) in fileList" :key="'file-' + (file.id || index)"
                                class="col-6 col-sm-4 col-md-3">
                                <div class="border border-light-subtle rounded-3 overflow-hidden c-pointer shadow-sm-hover transition-all bg-light"
                                    @click="previewMedia(file, 'file')">
                                    <div
                                        class="ratio ratio-16x9 bg-white border-bottom border-light-subtle d-flex align-items-center justify-content-center">
                                        <i :class="getFileIcon(file.file_name || file.file_type)"
                                            class="fa-2x text-dark opacity-50"></i>
                                    </div>
                                    <div class="p-2.5 bg-white">
                                        <div class="text-dark small fw-bold text-truncate mb-0.5">{{ file.file_name || `File
                                            ${index + 1}` }}</div>
                                        <div class="text-dark opacity-50" style="font-size: 0.7rem;">{{
                                            formatFileSize(file.file_size) }}</div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div v-else
                            class="text-center text-dark py-5 bg-light bg-opacity-25 rounded-3 border border-dashed">
                            <i class="fa fa-folder-open-o fa-2x mb-2 opacity-50"></i>
                            <p class="mb-0 small fw-bold opacity-75">No reference media files uploaded.</p>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Logs Timeline - col-md-12 -->
            <div class="row g-4">
                <div class="col-12">
                    <div class="card bg-white rounded-4 border border-light-subtle shadow-sm">
                        <div class="card-body">
                            <LogsTimeline :logs="order.logs || []" />
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Modal -->
        <div class="modal fade" id="poMediaModal" tabindex="-1">
            <div class="modal-dialog modal-dialog-centered modal-lg">
                <div class="modal-content border-0 overflow-hidden" style="border-radius:16px;">
                    <div class="modal-header py-3 border-bottom"
                        style="background:#fafafa;border-color:#ebebf0 !important;">
                        <h6 class="modal-title fw-semibold" style="font-size:13.5px;color:#1a1a2e;">
                            File Preview
                        </h6>
                        <button type="button" class="btn-close shadow-none" data-bs-dismiss="modal"></button>
                    </div>
                    <div class="modal-body p-0 bg-dark d-flex justify-content-center align-items-center"
                        style="min-height: 350px;">
                        <template v-if="previewType === 'image'">
                            <img :src="previewUrl" class="img-fluid object-fit-contain w-100"
                                style="max-height:75vh;" alt="Preview" />
                        </template>
                        <template v-else-if="previewType === 'file'">
                            <iframe :src="previewUrl" frameborder="0" class="w-100" style="height:70vh;"></iframe>
                        </template>
                    </div>
                    <div class="d-flex justify-content-end gap-2 mt-2 m-2">
                        <a v-if="previewType === 'file' && previewUrl" :href="previewUrl" target="_blank"
                            class="btn btn-sm btn-primary px-3 shadow-none mb-3">
                            <i class="fa fa-external-link me-1"></i> Open in New Tab
                        </a>
                        <button class="action-btn action-btn-back" data-bs-dismiss="modal"
                            style="height:32px;font-size:12px;padding:0 14px;">
                            Close
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { form_api_submit } from '@/utils/FormSubmit'
import { resolveFileUrl as resolveAssetFileUrl } from '@/utils/fileUrl'
import LoadingBar from '@/components/utils/LoadingBar.vue'
import PoStatus from '@/components/PoStatus.vue'
import LogsTimeline from '@/components/LogsTimeline.vue'

export default {
    name: 'PurchaseOrderProfile',
    components: { LoadingBar, PoStatus, LogsTimeline },
    data() {
        return {
            order: {},
            loading: false,
            external_id: this.$route.params.external_id,
            previewUrl: '',
            previewType: ''
        }
    },
    computed: {
        vendorName() {
            const vendor = this.order.vendor || {}
            return [vendor.first_name, vendor.last_name].filter(Boolean).join(' ')
                || vendor.external_id || 'N/A'
        },
        contactList() {
             return this.order.purchase_order_vendor_contacts ||  []
        },
        imageList() { return this.order.images || [] },
        fileList()  { return this.order.files  || [] },
        orderUserRoles() {
            return this.order.purchase_order_user_roles || []
        },
        receiverTeam() {
            return this.orderUserRoles
                .filter(entry => String(entry.role).toLowerCase() === 'received_by')
                .reduce((users, entry) => {
                    const user = entry.user || { id: entry.user_id, first_name: entry.first_name, last_name: entry.last_name }
                    if (user && !users.some(existing => existing.id === user.id)) {
                        users.push(user)
                    }
                    return users
                }, [])
        },
        verificationTeam() {
            return this.orderUserRoles
                .filter(entry => String(entry.role).toLowerCase() === 'verification_by')
                .reduce((users, entry) => {
                    const user = entry.user || { id: entry.user_id, first_name: entry.first_name, last_name: entry.last_name }
                    if (user && !users.some(existing => existing.id === user.id)) {
                        users.push(user)
                    }
                    return users
                }, [])
        }
    },
    mounted() { this.fetchOrder() },
    methods: {
        goToPOList() {
            this.$router.push({ name: 'OrderList' })
        },
        editOrder(id) {
            this.$router.push({ name: 'OrderEdit', params: { external_id: id } })
        },
        getFullName(user) {
            if (!user) return 'N/A'
            if (typeof user === 'string' || typeof user === 'number') return String(user)
            return [user.first_name, user.last_name].filter(Boolean).join(' ')
                || user.external_id || 'N/A'
        },
        getItemName(item) {
            const m = item.get_material || item.itemable || {}
            return m.name || m.code || m.external_id || `Item #${item.itemable_id || item.id}`
        },
        getItemCode(item) {
            const m = item.get_material || item.itemable || {}
            return m.code || m.external_id || ''
        },
        getVendorRoute(vendor) {
            const externalId = vendor?.external_id || vendor?.id
            return externalId ? { name: 'VendorProfile', params: { external_id: externalId } } : null
        },
        getItemRoute(item) {
            const entity = item?.get_material || item?.itemable || item?.material || item?.material_data || {}
            const externalId = entity.external_id || entity.id
            return externalId ? { name: 'MaterialProfile', params: { external_id: externalId } } : null
        },
        formatDate(value) {
            if (!value) return 'N/A'
            return new Date(value).toLocaleDateString('en-GB', {
                day: '2-digit', month: 'short', year: 'numeric'
            })
        },
        formatFileSize(bytes) {
            if (!bytes) return 'Unknown'
            const kb = bytes / 1024
            return kb < 1024 ? `${kb.toFixed(1)} KB` : `${(kb / 1024).toFixed(1)} MB`
        },
        formatCurrency(amount) {
            if (amount === null || amount === undefined || amount === '') return '$0.00'
            return `$${Number(amount).toFixed(2)}`
        },
        getFileIcon(fileName) {
            if (!fileName) return 'fa fa-file'
            const ext = String(fileName).split('.').pop().toLowerCase()
            const map = {
                pdf:  'fa fa-file-pdf-o',
                doc:  'fa fa-file-word-o', docx: 'fa fa-file-word-o',
                xls:  'fa fa-file-excel-o', xlsx: 'fa fa-file-excel-o', csv: 'fa fa-file-excel-o',
                ppt:  'fa fa-file-powerpoint-o', pptx: 'fa fa-file-powerpoint-o',
                jpg:  'fa fa-file-image-o', jpeg: 'fa fa-file-image-o', png: 'fa fa-file-image-o',
                gif:  'fa fa-file-image-o', webp: 'fa fa-file-image-o',
                txt:  'fa fa-file-text-o', md: 'fa fa-file-text-o',
                zip:  'fa fa-file-archive-o', rar: 'fa fa-file-archive-o'
            }
            return map[ext] || 'fa fa-file-o'
        },
        resolveFileUrl(path) {
            return resolveAssetFileUrl(path)
        },
        previewMedia(file, type) {
            const fileUrl = this.resolveFileUrl(file.file_path)

            if (type === 'image') {
                this.previewType = 'image'
                this.previewUrl = fileUrl
            } else {
                const ext = this.getFileExtension(file.file_name || file.file_path)
                const officeFormats = ['doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx']

                if (officeFormats.includes(ext)) {
                    this.previewType = 'file'
                    this.previewUrl = `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(fileUrl)}`
                } else if (ext === 'pdf') {
                    this.previewType = 'file'
                    this.previewUrl = fileUrl
                } else {
                    window.open(fileUrl, '_blank')
                    return
                }
            }
            const el = document.getElementById('poMediaModal')
            if (el) {
                const Modal = window.bootstrap?.Modal || window.Modal
                if (Modal) new Modal(el).show()
            }
        },
        getFileExtension(name = '') {
            return String(name).split('.').pop().toLowerCase()
        },
        async fetchOrder() {
            this.loading = true
            try {
                const result = await form_api_submit({
                    url: `purchase-order/profile/${this.external_id}`,
                    method: 'GET',
                    data: {},
                    show_success_toast: false,
                    show_error_toast: true
                })
                if (result.success) this.order = result.data || {}
            } catch (e) {
                console.error('Error fetching order:', e)
            } finally {
                this.loading = false
            }
        }
    }
}
</script>

<style scoped>
/* Modern Micro Clean-Ups replacing raw gray lines */
.rounded-4 {
    border-radius: 0.85rem !important;
}

.p-2\.5 {
    padding: 0.65rem !important;
}

.gap-1\.5 {
    gap: 0.35rem !important;
}

.gap-2\.5 {
    gap: 0.65rem !important;
}

.c-pointer {
    cursor: pointer;
}

.btn-white {
    background-color: #ffffff;
}

.shadow-sm-hover:hover {
    transform: translateY(-2px);
    box-shadow: 0 .5rem 1rem rgba(0, 0, 0, .06) !important;
}

.transition-all {
    transition: all 0.2s ease-in-out;
}

.custom-modern-table tbody tr {
    border-bottom: 1px solid #f8f9fa;
    transition: background-color 0.15s ease;
        background: #ffffff;
    }

    .custom-modern-table thead {
        background: #f8f9fa;
    }

    .custom-modern-table thead th {
        background: #f8f9fa;
    }

    .custom-modern-table tbody tr:hover {
        background-color: rgba(248, 249, 250, 0.6);
    }

    .table-sm.table-borderless.align-middle.mb-0 thead {
        background: #f8f9fa;
    }

    .table-sm.table-borderless.align-middle.mb-0 tbody tr {
        background: #ffffff;
        display: table-row !important;
    }

    .table-sm.table-borderless.align-middle.mb-0 tbody tr td {
        background: #ffffff;
        display: table-cell !important;
        vertical-align: middle;
        padding: 0.75rem 0.2rem;
    }

    .profile-item-card {
        border: 1px solid #e5e7eb;
        border-radius: 1rem;
        background: #f8fbff;
        overflow: hidden;
    }

    .profile-item-card-header {
        gap: 1rem;
        padding: 1rem;
        background: rgba(56, 113, 242, 0.04);
    }

    .profile-item-info {
        flex: 1 1 50%;
        min-width: 0;
    }

    .item-number {
        width: 36px;
        height: 36px;
        border-radius: 50%;
        background: #e7f0ff;
        color: #2563eb;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        font-weight: 700;
        box-shadow: 0 1px 2px rgba(15, 23, 42, 0.08);
        margin-top: 0.25rem;
    }

    .item-name {
        font-weight: 600;
        color: #111827;
    }

    .item-code {
        font-size: 0.8rem;
        color: #6b7280;
    }

    .profile-item-summary {
        justify-content: flex-end;
    }

    .summary-block {
        min-width: 110px;
    }

    .summary-label {
        font-size: 0.75rem;
        color: #6b7280;
    }

    .summary-value {
        font-weight: 600;
        color: #111827;
    }

.italic {
    font-style: italic;
}

@media (min-width: 768px) {
    .border-start-md {
        border-left: 1px solid var(--bs-border-color) !important;
    }

    .verify-checklist-card {
        padding: 1rem;
        border-radius: 1rem;
        background: #f8fafc;
        border: 1px solid #e5e7eb;
        margin: 0;
    }

    .verify-checklist-title {
        font-weight: 600;
        color: #111827;
        margin-bottom: 0.75rem;
    }

    .verify-checklist-grid {
        display: grid;
        gap: 0.5rem;
    }

    .verify-checklist-row {
        display: grid;
        grid-template-columns: auto 1fr auto;
        gap: 1rem;
        align-items: center;
        padding: 0.75rem;
        border-radius: 0.75rem;
        background: #ffffff;
        border: 1px solid #e5e7eb;
    }

    .verify-checklist-index {
        width: 28px;
        height: 28px;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        border-radius: 50%;
        background: #eff6ff;
        color: #1d4ed8;
        font-size: 0.85rem;
    }

    .verify-checklist-label {
        color: #334155;
        font-size: 0.95rem;
    }

    .verify-checklist-value {
        text-align: right;
        font-size: 12px;
        text-transform: uppercase;
    }
}
</style>